import { auctionHousesStore } from '@hds/auto-auction-com-client-sdk'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

/**
 * Which house the person stands in, for the Auction Operations context.
 *
 * One owner for a fact two places read: the shell's house switcher shows it,
 * and the Live Sales page filters by it. The roster is the client SDK's
 * `auctionHousesStore`, which reads the administration server's active Auction
 * Houses — the one reader of them, shared with the buyer app. A sale names its
 * house by that house's slug (`auctionHouseCode`), and the SDK store's `code`
 * is that slug.
 *
 * It is not the administration SDK's own Auction Houses store: that one holds
 * the page the Platform Administration list is showing, and a second reader
 * would overwrite it.
 *
 * The pick persists under `administration-house`, the shell's own prefix.
 * A remembered house that is no longer on the roster, or no pick at all,
 * resolves to the first house, so the page always stands somewhere.
 */
const STORAGE_KEY = 'administration-house'

function readStored() {
    try {
        return globalThis.localStorage?.getItem(STORAGE_KEY) ?? ''
    } catch {
        return ''
    }
}

function persist(code) {
    try {
        globalThis.localStorage?.setItem(STORAGE_KEY, code)
    } catch {
        // Storage refused (private mode, quota): the pick still holds for this page load.
    }
}

export const useOperationsContextStore = defineStore('operations-context', function () {
    const roster = auctionHousesStore()
    const selected = ref(readStored())

    /** `[{ code, name }]` — what the switcher lists. */
    const houses = computed(() => roster.houses.map((house) => ({ code: house.code, name: house.name })))

    /** The selected house's code, or the first house's when nothing valid is selected. */
    const houseCode = computed(function () {
        if (houses.value.some((house) => house.code === selected.value)) {
            return selected.value
        }
        return houses.value[0]?.code ?? ''
    })

    const houseName = computed(() => houses.value.find((house) => house.code === houseCode.value)?.name ?? '')

    function selectHouse(code) {
        selected.value = code
        persist(code)
    }

    /** Load the roster once. Never throws — the SDK's store degrades to an empty roster and tries again on the next ask. */
    function ensureHouses() {
        return roster.ensureLoaded()
    }

    return { houses, houseCode, houseName, selectHouse, ensureHouses }
})
